// src/services/event-service.js

import { createContext, useContext, useEffect, useState } from 'react';

import { db, analytics, timestamp, auth } from '../utils/firebase';

// Create a context for event logging
const EventContext = createContext();

// Custom hook to use the event logger
export const useEventLogger = () => useContext(EventContext);

const useUser = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((newUser) => {
      setUser(newUser);
    });

    return () => unsubscribe();
  }, []);

  return user;
};

// Provider component for event logging
export const EventProvider = ({ children }) => {
  const user = useUser();
  // const { user } = useAuthContext();

  // Log the user signing in
  useEffect(() => {
    const handler = async () => handleUserChanged(user);
    handler();
  }, [user]);

  async function handleUserChanged(_user) {
    if (!_user) return;

    const userId = _user.uid;
    const userDocRef = db.collection('users').doc(userId);
    const userDoc = await userDocRef.get();

    const now = new Date();
    const today = now.toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

    const lastLogin = userDoc.exists ? userDoc.data().lastLogin : null;

    // User has already logged in today, so don't log again
    if (lastLogin && lastLogin.startsWith(today)) {
      console.log('User has already logged in today');
      return;
    }
    // If the user hasn't logged in today, log the event and update lastLogin
    analytics.setUserId(userId);
    logEvent(EVENTS.user.auth.login);

    await userDocRef.set({ lastLogin: today }, { merge: true });
  }

  const logEvent = async (eventName, eventData) => {
    try {
      if (!user) {
        console.log('User not logged in. Event not logged.');
        return;
      }
      await logEventToFirestore(eventName, eventData);
    } catch (error) {
      console.error('Error logging event:', error?.message);
    }
  };

  const logEventToFirestore = async (type, data) => {
    try {
      // Additional logic to log events to Firestore
      // store events in a 'events' collection
      await db.collection('user_events').add({
        user_id: user.uid,
        type,
        ...(data && { data }),
        timestamp: timestamp(),
      });
    } catch (error) {
      console.error('Error logging event to Firestore:', error);
      throw error; // Throw error for handling in calling function
    }
  };

  const eventLogger = {
    logEvent,
  };

  return <EventContext.Provider value={eventLogger}>{children}</EventContext.Provider>;
};

const EVENTS = {
  onboarding: {
    complete: 'onboarding_complete',
  },
  expense: {
    complete: 'onboarding_complete',
  },
  user: {
    auth: {
      login: 'login',
    },
    goal: {
      create: 'create_goal',
    },
  },
};
